import { AuthGuard } from "@elit-hair/elit-tools";
import { Icon } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowLeft } from "react-icons/tb";
import { z } from "zod";

import { Link as AppLink } from "@/components/link";
import { PERMISSIONS } from "@/constants/permissions";
import { NotAuthorizedPage } from "@/pages/not-authorized/-index";
import { DataExportDatasetRow } from "@/pages/performance/call-exports/components/data-export-dataset-row";
import { DATA_EXPORT_DATASET_ORDER } from "@/pages/performance/call-exports/constants/data-export.constants";
import { ReportsWeekSelect } from "@/pages/performance/reports/components/reports-week-select";
import { REPORT_SURFACE_CLASS } from "@/pages/performance/reports/constants/report-ui";
import { getLastCompletedWeekAnchor } from "@/pages/performance/reports/utils/get-last-completed-week-anchor";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

const PAGE_MAX_WIDTH_CLASS = "max-w-5xl";

const DataExportSearchSchema = z.object({
    weekOf: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_DATA_EXPORTS)({
    component: DataExportsPage,
    validateSearch: zodValidator(DataExportSearchSchema),
    staticData: {
        config: {
            title: "data_exports",
            breadcrumb: {
                title: "data_exports",
                to: APP_ROUTES_TO.DEVELOPER_DATA_EXPORTS,
            },
        },
        permissions: [PERMISSIONS.CALL_DATA_EXPORT_DOWNLOAD],
    },
});

function DataExportsPage() {
    return (
        <AuthGuard required={[PERMISSIONS.CALL_DATA_EXPORT_DOWNLOAD]} fallback={<NotAuthorizedPage />}>
            <DataExportsContent />
        </AuthGuard>
    );
}

function resolveSelectedWeek(weekOf: string | undefined): DateTime {
    if (!weekOf) {
        return getLastCompletedWeekAnchor();
    }

    const parsed = DateTime.fromISO(weekOf);
    if (!parsed.isValid) {
        return getLastCompletedWeekAnchor();
    }

    return parsed.startOf("week");
}

function DataExportsContent() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const search = Route.useSearch({ structuralSharing: true });

    const selectedWeek = useMemo(() => resolveSelectedWeek(search.weekOf), [search.weekOf]);
    const weekOf = selectedWeek.toISODate();

    const handleWeekChange = useCallback(
        (date: DateTime) => {
            void navigate({
                search: {
                    weekOf: date.toISODate() ?? undefined,
                },
                replace: true,
            });
        },
        [navigate]
    );

    return (
        <div className={`mx-auto flex w-full ${PAGE_MAX_WIDTH_CLASS} flex-col gap-6`}>
            <div className="flex flex-col gap-2">
                <AppLink
                    to={APP_ROUTES_TO.DEVELOPER}
                    className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <Icon icon={TbArrowLeft} />
                    {t("developer")}
                </AppLink>
            </div>

            <section className={`${REPORT_SURFACE_CLASS} flex flex-col gap-6 p-5 sm:p-6`}>
                <ReportsWeekSelect selectedDate={selectedWeek} onDateChange={handleWeekChange} />

                <p className="text-sm text-muted-foreground">{t("developer_data_exports_description")}</p>

                <div key={weekOf ?? "week"} className="flex flex-col gap-6">
                    {DATA_EXPORT_DATASET_ORDER.map((dataset) => (
                        <DataExportDatasetRow key={dataset} dataset={dataset} weekOf={weekOf} />
                    ))}
                </div>
            </section>
        </div>
    );
}
